import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { format, subMonths } from 'date-fns';
import {
  DollarSign, Calendar, Building2, TrendingUp, Loader2, RefreshCw,
  Search, ChevronDown, ChevronRight, X, Mail, User, Clock,
  EyeOff, Eye, ArrowUpDown, Reply, Send,
} from 'lucide-react';
import {
  getBillingItems, startBillingScan, stopBillingScan,
  getBillingScanStatus, toggleBillingExclude, excludePayee,
} from '../../services/emailApi';

// ── Helpers ──────────────────────────────────────────────────────────────────

const CURRENCY_SYMBOLS = {
  USD: '$', EUR: '€', GBP: '£', JPY: '¥', CNY: '¥', KRW: '₩',
  INR: '₹', BRL: 'R$', MXN: '$', CAD: 'C$', AUD: 'A$', CHF: 'CHF ',
  SEK: 'kr', NOK: 'kr', DKK: 'kr', PLN: 'zł', CZK: 'Kč', HUF: 'Ft',
  TRY: '₺', ZAR: 'R', THB: '฿', PHP: '₱', IDR: 'Rp', MYR: 'RM',
  SGD: 'S$', HKD: 'HK$', NZD: 'NZ$', ILS: '₪', RON: 'lei',
  GTQ: 'Q', ARS: '$', COP: '$', PEN: 'S/', CRC: '₡', HNL: 'L',
  NIO: 'C$', PAB: 'B/.', DOP: 'RD$', JMD: 'J$', UYU: '$U',
  AED: 'د.إ', SAR: '﷼', QAR: '﷼', KWD: 'د.ك', BHD: '.د.ب',
};

function formatCurrency(amount, currency) {
  if (amount == null) return '—';
  const code = (currency || 'USD').toUpperCase();
  const sym = CURRENCY_SYMBOLS[code] || `${code} `;
  return `${sym}${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatUsd(amount) {
  if (amount == null) return '—';
  return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function getEffectiveUsd(item) {
  if (item.amount_usd != null) return item.amount_usd;
  return item.amount ?? 0;
}

function senderLabel(sender) {
  const match = sender.match(/^(.+?)\s*<.+>$/);
  return match ? match[1].replace(/^"|"$/g, '') : sender;
}

function sortItems(items, sort) {
  return [...items].sort((a, b) => {
    if (sort === 'amount') return getEffectiveUsd(b) - getEffectiveUsd(a);
    if (sort === 'payee') return (a.payee || '').localeCompare(b.payee || '');
    return new Date(b.email_date).getTime() - new Date(a.email_date).getTime();
  });
}

// ── Tiny shared components ───────────────────────────────────────────────────

function BillingTypeTag({ type }) {
  const colors = {
    subscription: 'bg-blue-100 text-blue-700',
    utility: 'bg-amber-100 text-amber-700',
    insurance: 'bg-green-100 text-green-700',
    service: 'bg-purple-100 text-purple-700',
    purchase: 'bg-pink-100 text-pink-700',
    tax: 'bg-red-100 text-red-700',
    membership: 'bg-indigo-100 text-indigo-700',
    other: 'bg-gray-100 text-gray-700',
  };
  const t = type || 'other';
  return (
    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${colors[t] || colors.other}`}>
      {t}
    </span>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center h-60 gap-3 text-gray-400">
      <DollarSign className="w-12 h-12 opacity-20" />
      <p className="text-sm font-medium">No billing data yet</p>
      <p className="text-xs max-w-xs text-center">
        Click &quot;Scan Emails&quot; above to search your email for bills, invoices, and receipts.
      </p>
    </div>
  );
}

// ── Scan status bar ──────────────────────────────────────────────────────────

function ScanStatusBar({ scanStatus, onStartScan, scanLoading }) {
  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 mb-6 bg-white border border-gray-200 rounded-xl px-4 py-3 shadow-sm">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <div className="text-sm">
          {scanStatus?.isRunning ? (
            <span className="flex items-center gap-2 text-blue-600">
              <Loader2 className="w-3.5 h-3.5 animate-spin flex-shrink-0" />
              <span className="hidden sm:inline">
                Scanning emails for bills... {scanStatus.billsFound} found ({scanStatus.totalProcessed} checked)
              </span>
              <span className="sm:hidden">Scanning... {scanStatus.billsFound} found</span>
            </span>
          ) : scanStatus?.isComplete ? (
            <span className="text-green-600">
              <span className="hidden sm:inline">
                Scan complete — {scanStatus.billsFound} bills found from {scanStatus.totalProcessed} emails
              </span>
              <span className="sm:hidden">{scanStatus.billsFound} bills found</span>
            </span>
          ) : scanStatus?.error ? (
            <span className="text-red-600">Scan error — will retry</span>
          ) : (
            <span className="text-gray-400">Scan your email for bills and invoices</span>
          )}
        </div>
      </div>
      <button
        className="w-full sm:w-auto flex-shrink-0 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={onStartScan}
        disabled={scanStatus?.isRunning || scanLoading}
      >
        {scanStatus?.isRunning ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <RefreshCw className="w-3.5 h-3.5" />
        )}
        {scanStatus?.isRunning ? 'Scanning...' : 'Scan Emails'}
      </button>
    </div>
  );
}

// ── Summary cards ────────────────────────────────────────────────────────────

function SummaryCards({ items }) {
  const stats = useMemo(() => {
    const now = new Date();
    const thisMonth = format(now, 'yyyy-MM');
    const lastMonth = format(subMonths(now, 1), 'yyyy-MM');
    const thisYear = format(now, 'yyyy');

    let thisMonthTotal = 0;
    let lastMonthTotal = 0;
    let thisYearTotal = 0;
    const payees = new Set();

    for (const item of items) {
      if (item.is_excluded) continue;
      const month = format(new Date(item.email_date), 'yyyy-MM');
      const year = format(new Date(item.email_date), 'yyyy');
      const amt = getEffectiveUsd(item);
      if (month === thisMonth) thisMonthTotal += amt;
      if (month === lastMonth) lastMonthTotal += amt;
      if (year === thisYear) thisYearTotal += amt;
      payees.add(item.payee);
    }

    return { thisMonthTotal, lastMonthTotal, thisYearTotal, uniquePayees: payees.size, totalBills: items.length };
  }, [items]);

  const cards = [
    { label: 'This Month', value: formatUsd(stats.thisMonthTotal) },
    { label: 'Last Month', value: formatUsd(stats.lastMonthTotal) },
    { label: 'This Year', value: formatUsd(stats.thisYearTotal) },
    { label: 'Payees', value: stats.uniquePayees },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
      {cards.map((c) => (
        <div key={c.label} className="bg-white border border-gray-200 rounded-xl px-4 py-3 shadow-sm">
          <p className="text-[11px] sm:text-[10px] text-gray-400 uppercase tracking-wider">{c.label}</p>
          <p className="text-base sm:text-lg font-bold text-gray-900">{c.value}</p>
        </div>
      ))}
    </div>
  );
}

// ── Billing row ──────────────────────────────────────────────────────────────

function BillingRow({ item, showPayee = true, indent = false, onItemClick, onToggleExclude, excludeLoading }) {
  return (
    <div
      className={`flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-3 sm:py-2.5 hover:bg-gray-50 transition-colors min-h-[48px] ${indent ? 'pl-4 sm:pl-8' : ''} ${item.is_excluded ? 'opacity-40' : ''}`}
    >
      <button
        className="flex-1 min-w-0 cursor-pointer text-left"
        onClick={() => onItemClick?.(item.id)}
      >
        <div className="flex items-center gap-2 flex-wrap">
          {showPayee && (
            <span className={`text-sm font-medium truncate ${item.is_excluded ? 'line-through text-gray-400' : 'text-gray-900'}`}>
              {item.payee}
            </span>
          )}
          <BillingTypeTag type={item.billing_type} />
        </div>
        <p className="text-xs text-gray-400 truncate mt-0.5">{item.subject}</p>
      </button>
      <div className="text-right flex-shrink-0">
        <p className={`text-sm font-semibold ${item.is_excluded ? 'line-through text-gray-400' : 'text-gray-900'}`}>
          {formatCurrency(item.amount, item.currency)}
        </p>
        {item.currency && item.currency.toUpperCase() !== 'USD' && item.amount_usd != null && (
          <p className="text-[10px] text-gray-400">≈ {formatUsd(item.amount_usd)}</p>
        )}
        <p className="text-[10px] text-gray-400">{format(new Date(item.email_date), 'MMM d, yyyy')}</p>
      </div>
      <button
        className="flex-shrink-0 w-11 h-11 sm:w-7 sm:h-7 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-900 transition-colors"
        onClick={(e) => {
          e.stopPropagation();
          onToggleExclude(item.id);
        }}
        title={item.is_excluded ? 'Include in reports' : 'Exclude from reports'}
      >
        {excludeLoading ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : item.is_excluded ? (
          <Eye className="w-3.5 h-3.5" />
        ) : (
          <EyeOff className="w-3.5 h-3.5" />
        )}
      </button>
    </div>
  );
}

// ── Monthly view ─────────────────────────────────────────────────────────────

function MonthlyView({ items, onItemClick, sortMode, onToggleExclude, excludeLoading }) {
  const [expandedMonth, setExpandedMonth] = useState(null);

  const monthlyData = useMemo(() => {
    const grouped = new Map();
    for (const item of items) {
      const month = format(new Date(item.email_date), 'yyyy-MM');
      const existing = grouped.get(month) || { items: [], total: 0 };
      existing.items.push(item);
      if (!item.is_excluded) existing.total += getEffectiveUsd(item);
      grouped.set(month, existing);
    }
    return Array.from(grouped.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [items]);

  if (monthlyData.length === 0) return <EmptyState />;

  return (
    <div className="space-y-2">
      {monthlyData.map(([month, data]) => (
        <div key={month} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          <button
            className="w-full flex items-center justify-between gap-2 px-4 py-3.5 sm:py-3 hover:bg-gray-50 transition-colors min-h-[48px]"
            onClick={() => setExpandedMonth(expandedMonth === month ? null : month)}
          >
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              {expandedMonth === month ? (
                <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
              )}
              <Calendar className="w-4 h-4 text-blue-500 flex-shrink-0" />
              <span className="font-medium text-sm truncate">
                {format(new Date(month + '-01'), 'MMMM yyyy')}
              </span>
              <span className="text-xs text-gray-400 flex-shrink-0">
                {data.items.length} bill{data.items.length !== 1 ? 's' : ''}
              </span>
            </div>
            <span className="font-bold text-sm flex-shrink-0">{formatUsd(data.total)}</span>
          </button>
          {expandedMonth === month && (
            <div className="border-t border-gray-200">
              {sortItems(data.items, sortMode).map((item) => (
                <BillingRow
                  key={item.id}
                  item={item}
                  onItemClick={onItemClick}
                  onToggleExclude={onToggleExclude}
                  excludeLoading={excludeLoading}
                />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Payee view ───────────────────────────────────────────────────────────────

function PayeeView({ items, onItemClick, sortMode, onToggleExclude, excludeLoading, onExcludePayee, excludePayeeLoading }) {
  const [expandedPayee, setExpandedPayee] = useState(null);

  const payeeData = useMemo(() => {
    const grouped = new Map();
    for (const item of items) {
      const payee = item.payee;
      const existing = grouped.get(payee) || { items: [], total: 0, monthlyAvg: 0, allExcluded: true };
      existing.items.push(item);
      if (!item.is_excluded) {
        existing.total += getEffectiveUsd(item);
        existing.allExcluded = false;
      }
      grouped.set(payee, existing);
    }
    for (const [, data] of grouped) {
      const months = new Set(
        data.items.filter((i) => !i.is_excluded).map((i) => format(new Date(i.email_date), 'yyyy-MM'))
      );
      data.monthlyAvg = months.size > 0 ? data.total / months.size : data.total;
    }
    return Array.from(grouped.entries()).sort((a, b) => b[1].total - a[1].total);
  }, [items]);

  if (payeeData.length === 0) return <EmptyState />;

  return (
    <div className="space-y-2">
      {payeeData.map(([payee, data]) => (
        <div
          key={payee}
          className={`bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm ${data.allExcluded ? 'opacity-50' : ''}`}
        >
          <div className="flex items-center">
            <button
              className="flex-1 flex items-center justify-between gap-2 px-3 sm:px-4 py-3.5 sm:py-3 hover:bg-gray-50 transition-colors min-h-[48px] min-w-0"
              onClick={() => setExpandedPayee(expandedPayee === payee ? null : payee)}
            >
              <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-wrap">
                {expandedPayee === payee ? (
                  <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                )}
                <Building2 className="w-4 h-4 text-purple-500 flex-shrink-0 hidden sm:block" />
                <span className={`font-medium text-sm truncate ${data.allExcluded ? 'line-through text-gray-400' : ''}`}>
                  {payee}
                </span>
                <span className="text-xs text-gray-400 flex-shrink-0">
                  {data.items.length} bill{data.items.length !== 1 ? 's' : ''}
                </span>
                {!data.allExcluded && (
                  <span className="text-xs text-gray-400 flex-shrink-0 hidden sm:inline">
                    ~{formatUsd(data.monthlyAvg)}/mo
                  </span>
                )}
              </div>
              <span className={`font-bold text-sm flex-shrink-0 ${data.allExcluded ? 'line-through text-gray-400' : ''}`}>
                {formatUsd(data.total)}
              </span>
            </button>
            <button
              className="mr-1 sm:mr-2 flex-shrink-0 text-xs min-h-[44px] sm:min-h-0 inline-flex items-center gap-1 px-2 py-1 rounded-md text-gray-500 hover:bg-gray-100 transition-colors disabled:opacity-50"
              onClick={(e) => {
                e.stopPropagation();
                onExcludePayee(payee, !data.allExcluded);
              }}
              disabled={excludePayeeLoading}
            >
              {excludePayeeLoading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : data.allExcluded ? (
                <Eye className="w-3.5 h-3.5 sm:mr-1" />
              ) : (
                <EyeOff className="w-3.5 h-3.5 sm:mr-1" />
              )}
              <span className="hidden sm:inline">{data.allExcluded ? 'Include' : 'Exclude'}</span>
            </button>
          </div>
          {expandedPayee === payee && (
            <div className="border-t border-gray-200">
              {sortItems(data.items, sortMode).map((item) => (
                <BillingRow
                  key={item.id}
                  item={item}
                  showPayee={false}
                  onItemClick={onItemClick}
                  onToggleExclude={onToggleExclude}
                  excludeLoading={excludeLoading}
                />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Yearly view ──────────────────────────────────────────────────────────────

function YearlyView({ items, onItemClick, sortMode, onToggleExclude, excludeLoading }) {
  const [expandedYear, setExpandedYear] = useState(null);

  const yearlyData = useMemo(() => {
    const grouped = new Map();
    for (const item of items) {
      const year = format(new Date(item.email_date), 'yyyy');
      const month = format(new Date(item.email_date), 'yyyy-MM');
      if (!grouped.has(year)) grouped.set(year, { months: new Map(), total: 0 });
      const yearData = grouped.get(year);
      if (!yearData.months.has(month)) yearData.months.set(month, { items: [], total: 0 });
      const monthData = yearData.months.get(month);
      monthData.items.push(item);
      if (!item.is_excluded) {
        const usd = getEffectiveUsd(item);
        monthData.total += usd;
        yearData.total += usd;
      }
    }
    return Array.from(grouped.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [items]);

  if (yearlyData.length === 0) return <EmptyState />;

  return (
    <div className="space-y-4">
      {yearlyData.map(([year, data]) => (
        <div key={year} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          <button
            className="w-full flex items-center justify-between gap-2 px-4 py-3.5 sm:py-3 hover:bg-gray-50 transition-colors min-h-[48px]"
            onClick={() => setExpandedYear(expandedYear === year ? null : year)}
          >
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              {expandedYear === year ? (
                <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
              )}
              <TrendingUp className="w-4 h-4 text-green-500 flex-shrink-0" />
              <span className="font-bold text-base">{year}</span>
              <span className="text-xs text-gray-400 flex-shrink-0">
                {data.months.size} months &middot;{' '}
                {Array.from(data.months.values()).reduce((s, m) => s + m.items.length, 0)} bills
              </span>
            </div>
            <span className="font-bold text-base flex-shrink-0">{formatUsd(data.total)}</span>
          </button>
          {expandedYear === year && (
            <div className="border-t border-gray-200">
              {Array.from(data.months.entries())
                .sort((a, b) => b[0].localeCompare(a[0]))
                .map(([month, mData]) => (
                  <div key={month} className="border-b border-gray-100 last:border-b-0">
                    <div className="flex items-center justify-between gap-2 px-4 sm:px-6 py-2 bg-gray-50">
                      <span className="text-xs font-medium text-gray-400">
                        {format(new Date(month + '-01'), 'MMMM')}
                      </span>
                      <span className="text-xs font-bold">{formatUsd(mData.total)}</span>
                    </div>
                    {sortItems(mData.items, sortMode).map((item) => (
                      <BillingRow
                        key={item.id}
                        item={item}
                        indent
                        onItemClick={onItemClick}
                        onToggleExclude={onToggleExclude}
                        excludeLoading={excludeLoading}
                      />
                    ))}
                  </div>
                ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Email detail modal ───────────────────────────────────────────────────────

function BillingEmailModal({ itemId, item, onClose, onToggleExclude, excludeLoading }) {
  const [emailContent, setEmailContent] = useState(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [showReply, setShowReply] = useState(false);
  const [replyText, setReplyText] = useState('');

  useEffect(() => {
    if (itemId == null) return;
    setContentLoading(true);
    // Fetch billing item email content via api service pattern
    fetch(`/api/webmail/billing/items/${itemId}/content`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('webmail_token')}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => setEmailContent(data))
      .catch(() => setEmailContent(null))
      .finally(() => setContentLoading(false));
  }, [itemId]);

  const handleClose = () => {
    setShowReply(false);
    setReplyText('');
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(15,23,42,0.45)' }}
      onClick={handleClose}
    >
      <div
        className="relative bg-white sm:rounded-2xl shadow-2xl flex flex-col w-full h-full sm:w-auto sm:h-auto"
        style={{ maxWidth: 'min(780px, 96vw)', maxHeight: '100vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-4 sm:px-6 pt-4 sm:pt-5 pb-3 sm:pb-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <h2 className="text-sm sm:text-base font-semibold text-gray-900 leading-snug mb-2 pr-6">
                {item?.subject ?? 'Loading...'}
              </h2>
              <div className="flex flex-wrap items-center gap-x-3 sm:gap-x-4 gap-y-1 text-xs text-gray-400">
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {item ? senderLabel(item.sender) : ''}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {item ? format(new Date(item.email_date), 'MMM d, yyyy h:mm a') : ''}
                </span>
              </div>
              {item && (
                <div className="mt-2 flex items-center gap-2">
                  <BillingTypeTag type={item.billing_type} />
                  <span className="text-sm font-bold">{formatCurrency(item.amount, item.currency)}</span>
                  {item.currency && item.currency.toUpperCase() !== 'USD' && item.amount_usd != null && (
                    <span className="text-xs text-gray-400">≈ {formatUsd(item.amount_usd)}</span>
                  )}
                  {item.payee && <span className="text-xs text-gray-400">from {item.payee}</span>}
                </div>
              )}
            </div>
            <button
              className="flex-shrink-0 w-11 h-11 sm:w-7 sm:h-7 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 transition-colors"
              onClick={handleClose}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {contentLoading ? (
            <div className="flex items-center justify-center h-48 gap-3 text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">Loading email...</span>
            </div>
          ) : !emailContent?.body ? (
            <div className="flex flex-col items-center justify-center h-48 gap-2 text-gray-400">
              <Mail className="w-8 h-8 opacity-30" />
              <p className="text-sm">No content available</p>
            </div>
          ) : emailContent.bodyType === 'html' ? (
            <iframe
              srcDoc={`<base target="_blank">${emailContent.body}`}
              sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
              className="w-full border-0"
              style={{ minHeight: 400, height: '100%', display: 'block' }}
              title="Billing email content"
            />
          ) : (
            <pre className="text-sm text-gray-900 whitespace-pre-wrap break-words leading-relaxed p-6 font-sans">
              {emailContent.body}
            </pre>
          )}
        </div>

        {/* Reply area */}
        {showReply && (
          <div className="px-4 sm:px-6 py-3 border-t border-gray-200 flex-shrink-0 bg-blue-50/50">
            <div className="flex items-center gap-2 mb-2 text-xs text-gray-400">
              <Reply className="w-3.5 h-3.5" />
              <span>Replying to {item ? senderLabel(item.sender) : '...'}</span>
            </div>
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Type your reply..."
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500 resize-none bg-white"
              rows={4}
              autoFocus
            />
            <div className="flex items-center justify-end gap-2 mt-2">
              <button
                className="px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-100 rounded-md transition-colors"
                onClick={() => {
                  setShowReply(false);
                  setReplyText('');
                }}
              >
                Cancel
              </button>
              <button
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                onClick={() => {
                  // Reply functionality placeholder — wire up when backend supports it
                  setShowReply(false);
                  setReplyText('');
                }}
                disabled={!replyText.trim()}
              >
                <Send className="w-3.5 h-3.5" />
                Send
              </button>
            </div>
          </div>
        )}

        {/* Footer actions */}
        <div className="flex items-center justify-between gap-2 px-4 sm:px-6 py-3 border-t border-gray-200 flex-shrink-0 bg-gray-50 sm:rounded-b-2xl flex-wrap">
          <button
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-md hover:bg-gray-100 transition-colors min-h-[44px] sm:min-h-0"
            onClick={() => setShowReply(!showReply)}
          >
            <Reply className="w-3.5 h-3.5 sm:mr-1.5" />
            <span className="hidden sm:inline">Reply</span>
          </button>
          <button
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors min-h-[44px] sm:min-h-0 ${
              item?.is_excluded
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'border border-gray-300 hover:bg-gray-100'
            } disabled:opacity-50`}
            onClick={() => onToggleExclude(itemId)}
            disabled={excludeLoading}
          >
            {excludeLoading ? (
              <Loader2 className="w-3.5 h-3.5 sm:mr-1.5 animate-spin" />
            ) : item?.is_excluded ? (
              <Eye className="w-3.5 h-3.5 sm:mr-1.5" />
            ) : (
              <EyeOff className="w-3.5 h-3.5 sm:mr-1.5" />
            )}
            <span className="hidden sm:inline">
              {item?.is_excluded ? 'Include in Reports' : 'Exclude from Reports'}
            </span>
            <span className="sm:hidden">{item?.is_excluded ? 'Include' : 'Exclude'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

export default function BillingView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanStatus, setScanStatus] = useState(null);
  const [scanLoading, setScanLoading] = useState(false);
  const [excludeLoading, setExcludeLoading] = useState(false);
  const [excludePayeeLoading, setExcludePayeeLoading] = useState(false);
  const [viewMode, setViewMode] = useState('monthly');
  const [sortMode, setSortMode] = useState('date');
  const [selectedItemId, setSelectedItemId] = useState(null);
  const pollRef = useRef(null);

  // Fetch billing items
  const fetchItems = useCallback(async () => {
    try {
      const data = await getBillingItems();
      setItems(data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch scan status
  const fetchScanStatus = useCallback(async () => {
    try {
      const data = await getBillingScanStatus();
      setScanStatus(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchItems();
    fetchScanStatus();
  }, [fetchItems, fetchScanStatus]);

  // Poll scan status while running
  useEffect(() => {
    if (scanStatus?.isRunning) {
      pollRef.current = setInterval(async () => {
        const status = await fetchScanStatus();
        if (status && !status.isRunning) {
          clearInterval(pollRef.current);
          fetchItems(); // Refresh items after scan completes
        }
      }, 3000);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [scanStatus?.isRunning, fetchScanStatus, fetchItems]);

  // Start scan
  const handleStartScan = async () => {
    setScanLoading(true);
    try {
      await startBillingScan();
      await fetchScanStatus();
    } catch {
      // ignore
    } finally {
      setScanLoading(false);
    }
  };

  // Toggle exclude on a single item
  const handleToggleExclude = async (id) => {
    setExcludeLoading(true);
    try {
      await toggleBillingExclude(id);
      await fetchItems();
    } catch {
      // ignore
    } finally {
      setExcludeLoading(false);
    }
  };

  // Exclude all items for a payee
  const handleExcludePayee = async (payee, exclude) => {
    setExcludePayeeLoading(true);
    try {
      await excludePayee(payee, exclude);
      await fetchItems();
    } catch {
      // ignore
    } finally {
      setExcludePayeeLoading(false);
    }
  };

  const allItems = items;
  const selectedItem = allItems.find((i) => i.id === selectedItemId);

  const viewModes = [
    { key: 'monthly', label: 'Monthly', Icon: Calendar },
    { key: 'payee', label: 'By Payee', Icon: Building2 },
    { key: 'yearly', label: 'Yearly', Icon: TrendingUp },
  ];

  const sortModes = [
    { key: 'date', label: 'Date' },
    { key: 'amount', label: 'Amount' },
    { key: 'payee', label: 'Payee' },
  ];

  return (
    <div className="flex-1 overflow-y-auto p-4 sm:p-6">
      <ScanStatusBar scanStatus={scanStatus} onStartScan={handleStartScan} scanLoading={scanLoading} />

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      ) : (
        <>
          <SummaryCards items={allItems} />

          {/* View mode + sort toggles */}
          <div className="flex items-center gap-3 mb-4 overflow-x-auto pb-1 -mx-4 px-4 sm:mx-0 sm:px-0 sm:overflow-visible sm:flex-wrap">
            <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg p-1 flex-shrink-0 shadow-sm">
              {viewModes.map(({ key, label, Icon }) => (
                <button
                  key={key}
                  onClick={() => setViewMode(key)}
                  className={`flex items-center gap-1.5 px-3 py-2 sm:py-1.5 rounded-md text-xs font-medium transition-colors min-h-[44px] sm:min-h-0 ${
                    viewMode === key
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-400 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg p-1 flex-shrink-0 shadow-sm">
              <ArrowUpDown className="w-3.5 h-3.5 text-gray-400 ml-2" />
              {sortModes.map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setSortMode(key)}
                  className={`flex items-center gap-1.5 px-3 py-2 sm:py-1.5 rounded-md text-xs font-medium transition-colors min-h-[44px] sm:min-h-0 ${
                    sortMode === key
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-400 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {viewMode === 'monthly' && (
            <MonthlyView
              items={allItems}
              onItemClick={setSelectedItemId}
              sortMode={sortMode}
              onToggleExclude={handleToggleExclude}
              excludeLoading={excludeLoading}
            />
          )}
          {viewMode === 'payee' && (
            <PayeeView
              items={allItems}
              onItemClick={setSelectedItemId}
              sortMode={sortMode}
              onToggleExclude={handleToggleExclude}
              excludeLoading={excludeLoading}
              onExcludePayee={handleExcludePayee}
              excludePayeeLoading={excludePayeeLoading}
            />
          )}
          {viewMode === 'yearly' && (
            <YearlyView
              items={allItems}
              onItemClick={setSelectedItemId}
              sortMode={sortMode}
              onToggleExclude={handleToggleExclude}
              excludeLoading={excludeLoading}
            />
          )}
        </>
      )}

      {selectedItemId !== null && (
        <BillingEmailModal
          itemId={selectedItemId}
          item={selectedItem}
          onClose={() => setSelectedItemId(null)}
          onToggleExclude={handleToggleExclude}
          excludeLoading={excludeLoading}
        />
      )}
    </div>
  );
}
